import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Preload Google GSI script as early as possible so it's ready when user clicks
const _preloadGSI = () => {
  if (document.getElementById('google-gsi-script')) return;
  const script = document.createElement('script');
  script.id = 'google-gsi-script';
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
};
_preloadGSI();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('kstreak_token'));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const storedToken = localStorage.getItem('kstreak_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('kstreak_token');
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // If 2FA required, surface the pre-auth payload to the caller — don't mint a session yet
    if (response.data?.requires_2fa) {
      return { requires_2fa: true, preauth_token: response.data.preauth_token };
    }
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kstreak_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const verifyTwoFactor = async (preauth_token, code) => {
    const response = await axios.post(`${API}/auth/2fa/verify`, { preauth_token, code });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kstreak_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'user') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kstreak_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // Load Google GSI script dynamically and trigger the OAuth popup.
    // On success, Google calls window.__kstreak_google_callback with the credential (ID token).
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set');
      return;
    }

    window.__kstreak_google_callback = async (response) => {
      if (!response?.credential) {
        alert('Google did not return a credential. Please try again.');
        return;
      }
      try {
        const res = await axios.post(`${API}/auth/google/session`, { id_token: response.credential }, { withCredentials: true });
        const { access_token, user: userData } = res.data;
        localStorage.setItem('kstreak_token', access_token);
        setToken(access_token);
        setUser(userData);
        window.location.href = '/dashboard';
      } catch (err) {
        console.error('Google login failed:', err);
        const detail = err?.response?.data?.detail || err?.message || 'Google sign-in failed';
        alert(`Sign-in error: ${detail}`);
      }
    };

    const _init = () => {
      if (!window.google) {
        // Script not ready yet — wait briefly and retry once
        setTimeout(_init, 300);
        return;
      }
      try { window.google.accounts.id.cancel(); } catch (_) {}
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: window.__kstreak_google_callback,
        ux_mode: 'popup',
        use_fedcm_for_prompt: false,
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const container = document.getElementById('g_id_signin_fallback');
          if (container && window.google) {
            window.google.accounts.id.renderButton(container, { type: 'standard', size: 'large' });
            const btn = container.querySelector('div[role=button]');
            if (btn) btn.click();
          }
        }
      });
    };

    // Script already preloaded on app start — just initialize
    _init();
  };

  const handleGoogleCallback = async (sessionId) => {
    // Legacy — kept for backward compatibility, no longer used in main flow
    const response = await axios.post(`${API}/auth/google/session`, { id_token: sessionId }, { withCredentials: true });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('kstreak_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('kstreak_token');
    localStorage.removeItem('kstreak_consent');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    verifyTwoFactor,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    checkAuth,
    setUser,
    setToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
